import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Box,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material';

const DiagnosticosTratamientos = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL;

  const columns = [
    { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 200 },
    { field: 'numid', headerName: 'Identificación', width: 150 },
    { 
      field: 'birthdate', 
      headerName: 'Nacimiento', 
      width: 110,
    },
    { 
      field: tabValue === 0 ? 'Diagnostico' : 
             tabValue === 1 ? 'Tratamiento' : 'Responsable', 
      headerName: tabValue === 0 ? 'Diagnóstico' : 
                 tabValue === 1 ? 'Tratamiento' : 'Médico', 
      flex: 2, 
      minWidth: 300 
    },
    { 
      field: 'FechaDeLaHistoria', 
      headerName: 'Fecha Historia', 
      width: 110,
    },
  ];

  const handleSearch = async () => {
    if (!searchTerm || !startDate || !endDate) return;
  
    try {
      // Normalizar el término de búsqueda eliminando tildes, espacios y convirtiendo a minúsculas
      const normalizedSearchTerm = searchTerm
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '');

      const tz = 'America/Costa_Rica';
      const formattedStart = startDate.startOf('day').tz(tz).format('YYYY-MM-DD HH:mm:ss');
      const formattedEnd = endDate.endOf('day').tz(tz).format('YYYY-MM-DD HH:mm:ss');
      
      let endpoint;
      if (tabValue === 0) {
        endpoint = 'diagnosticos';
      } else if (tabValue === 1) {
        endpoint = 'tratamientos';
      } else {
        endpoint = 'medicos'; // Nuevo endpoint para médicos
      }
      
      const response = await axios.get(`${API_URL}/api/${endpoint}`, {
        params: {
          search: normalizedSearchTerm,
          startDate: formattedStart,
          endDate: formattedEnd
        }
      });
  
      // Formatear las fechas de los resultados
      const formattedResults = response.data.map((result, index) => ({
        ...result,
        id: result.idpac || `temp-${index}`,
        FechaDeLaHistoria: result.FechaDeLaHistoria 
          ? dayjs(result.FechaDeLaHistoria).format('YYYY-MM-DD') 
          : '',
        birthdate: result.birthdate 
          ? dayjs(result.birthdate).format('YYYY-MM-DD') 
          : ''
      }));
  
      setResults(formattedResults);
  
      // Determinar el tipo de estadística según la pestaña seleccionada
      let tipo;
      if (tabValue === 0) {
        tipo = 'Diagnostico';
      } else if (tabValue === 1) {
        tipo = 'tratamiento';
      } else {
        tipo = 'Responsable'; // Nuevo tipo para médicos
      }

      const statsRes = await axios.get(`${API_URL}/api/estadisticas`, {
        params: {
          tipo: tipo,
          search: normalizedSearchTerm,
          startDate: formattedStart,
          endDate: formattedEnd
        }
      });
      setStats(statsRes.data.total);
    } catch (error) {
      console.error('Error en la búsqueda:', error);
    }
  };

  const handleRowClick = (params) => {
    navigate(`/historias/${params.row.idpac}`);
  };

  return (
<Box
                className="center-align"
                sx={{
                    display: 'flex',
                    padding: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    flexDirection: 'column'
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: { xs: 2, sm: 4 },
                        width: '100%',
                        maxWidth: '98vw',
                        overflowX: 'auto',
                        borderRadius: 2,
                        margin: 'auto',
                        boxSizing: 'border-box',
                        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        marginTop: '50px'
                    }}
                    >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Bioestadística
        </Typography>
                 
        <Tabs 
        value={tabValue} 
        onChange={(e, newValue) => {
            setTabValue(newValue);
            setSearchTerm(''); // Reinicia el término de búsqueda
            setResults([]);    // Reinicia los resultados del grid
            setStats(0);       // Reinicia las estadísticas
        }}
        variant="scrollable" // Permite scroll horizontal en tabs
        scrollButtons="auto" // Muestra botones de scroll en pantallas pequeñas
        >
        <Tab label="Diagnósticos" />
        <Tab label="Tratamientos" />
        {/* Nueva pestaña para médicos */}
        <Tab label="Médicos" />
        </Tabs>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ my: 2 }}>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <TextField
                label={
                  tabValue === 0 ? 'Buscar diagnóstico' : 
                  tabValue === 1 ? 'Buscar tratamiento' : 
                  'Buscar médico (ej: Dr. Carlos Espinoza)'}
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={tabValue === 2 ? "Ingrese nombre del médico" : ""}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Fecha inicial"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="YYYY-MM-DD"
                />
                <DatePicker
                  label="Fecha final"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="YYYY-MM-DD"
                />
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button 
            variant="contained" 
            onClick={handleSearch} 
            sx={{ 
              width: { xs: '100%', sm: 'auto' }, 
              gap: 2 
            }}
          >
            Buscar
          </Button>
          {stats > 0 && (
            <Chip 
              label={
                tabValue === 0 
                  ? `${stats} diagnósticos encontrados`
                  : tabValue === 1
                  ? `${stats} recetas encontradas`
                  : `${stats} historias encontradas`
              }
              color="primary"
              sx={{ 
                fontSize: '1rem',
                py: 2.5,
                px: 1,
                borderRadius: 2,
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                }
              }}
            />
          )}
        </Box>

        <div style={{ height: '50vh', width: '100%', minHeight: '300px' }}>
          <DataGrid
            rows={results}
            columns={columns}
            pageSize={10}
            onRowClick={handleRowClick}
            components={{ Toolbar: GridToolbar }}
            getRowId={(row) => row.idpac} // Usa 'idpac' como identificador único
            sx={{
                '& .MuiDataGrid-root': {
                  fontSize: { xs: '0.8rem', sm: '1rem' }, // Ajusta el tamaño de fuente en móviles
                },
              }}
          />
        </div>
      </Paper>
    </Box>
  );
};

export default DiagnosticosTratamientos;